// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable, runInAction } from 'mobx';
import { withStyles } from "tss-react/mui";
import { Dialog, Grid, Typography, DialogContent, DialogActions } from '@mui/material';
import cx from 'classnames';
import { cloneDeep } from 'lodash';

import Field from '@shared/components/Form/components/Field';
import { Check4CheckAll, date2yyyymmdd, getTestAttr, yyyymmdd2date } from '@shared/utils/common';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import DialogTitle from '@shared/components/DialogTitle';

import { Option, normalize } from '@shared/components/Select';
import { FilterItems } from '@shared/models/jobrequesttimelines';
import container from '@core/di';
import PlatformsStore from '@shared/stores/platforms';
import VendorsStore from '@shared/stores/vendors';
import { ROLES } from '@shared/constants/user';
import { VENDOR_USER_FIELDS } from '@shared/constants/vendor';
import { PLATFORM_FIELDS } from '@shared/constants/platform';
import { JOB_REQUEST_FILTER_LABELS, JOB_REQUEST_FILTERS, JOB_REQUEST_STATUS_LABELS, JobRequestStatus, PRIORITY_ENUM, PRIORITY_ENUM_Complete } from '@shared/constants/job-request';
import { PlatformDTO } from '@shared/models/platform';

import { VendorUsersDTO } from '@shared/models/vendor';
import { Inject, NodeCheckEventArgs, TreeViewComponent } from '@syncfusion/ej2-react-navigations';
import { CheckBoxSelection, MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars';
import { OrdersStore } from '@shared/stores';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { LABEL_CLEAR_ALL, LABEL_SELECT_ALL } from '@shared/constants/filters';
import AGMap from '@modules/Private/submodules/Map/components/AGMap';
import { MapMarkerDTO } from '@shared/models/gmap-data';
import { buildMarkersFromOrderID, computeBoundFromMarkers } from '@modules/Private/submodules/Map/components/shared/common';

// Prop interface for the main class.
export interface JobRequestFilterProps {
  className?: string;
  orderId?: number;                                           // orderID to query for orderAssets for Map filters.
  assetList?: Array<{id: number, name: string, tag: string}>; // List of assets for this order. Replacing call to this.orderStore.getAssetList(orderId) 
                                                              //   which may include assets not viewable to the current user.  TBD: also replace orderID (?).
  possibleFlags?: Array<Option>;                              // Values for flag selection box.
  staffIds?: Array<number>;                                   // List of staff names for this order.
  onFilter: (items: FilterItems) => any;                      // callback to handle onFilter event.
  filterInfo: FilterItems;                                    // filter info such as type, current selected items, ...
}

/*
  Component to display a Dialog to select items under a certain category (defined in props.filterInfo.type)
  to filter the list of job requests.
  Note: Workflow filter is not handled here.
*/
@observer
class JobRequestFilter extends React.Component<JobRequestFilterProps> {
  // Store objs to do I/O with portal-api.
  private orderStore = container.get<OrdersStore>(OrdersStore.diToken);
  private vendorsStore = container.get<VendorsStore>(VendorsStore.diToken);
  private platformStore = container.get<PlatformsStore>(PlatformsStore.diToken);

  // Stateful vars for controlling rendering.
  @observable isSubmitting = false;

  @observable users: { [role: string]: Array<Option> } = {};
  @observable platforms: Array<PlatformDTO> = [];
  
  @observable staffNodes: Array<any> = [];                                        // datasource for the filter staff tree.
  @observable assetList: Array<{id: number, name: string, tag: string}> = [];
  @observable tagSiteList: Array<{id: number, name: string, tag: string}> = []; 

  @observable center: {lat: number, lng: number}; // Center of the displayed area (hack to trigger a rerender)
  private markers: MapMarkerDTO[] = [];                                                 // List of markers to be shown on the map.
  private bounds: {latMin: number, latMax: number, lngMin: number, lngMax: number};     // Boundary for the displayed area.
  private selectAssetIdsByMap: number[] = [];

  // Note: Some Filters based on the original IntellectSoft using Material UI Form (validation with rules, ...)
  //   Others based on SyncFusion components and do not use Form (may need to add validation).
  private category: string;

  // Syncfusion DOM objs.
  private staffTree: TreeViewComponent | null = null;
  private tagTree: TreeViewComponent | null = null;
  private assetSelect: MultiSelectComponent | null = null;
  private statusSelect:  MultiSelectComponent | null = null;
  private flagSelect:  MultiSelectComponent | null = null;
  private plannedDatePicker: DateRangePickerComponent | null = null;

  private staffCheckbox: CheckBoxComponent | null = null;
  private tagCheckbox: CheckBoxComponent | null = null;

  // Initialize the form and load data to build items for the filter selection boxes (pilots, observers, ... for 'Staff' filter, jobReqs, delis for 'Site' filter, ...).
  constructor(props: JobRequestFilterProps) {
    super(props);
    //console.log("*** JRFilter props:", props);

    // Default map bounds and center
    this.center = {lat: 39.64179518191567, lng: -98.3302019912498};
    this.bounds = {latMin: 16.639000880823524, latMax: 56.946573247221494, lngMin: -124.6973894912498, lngMax: -71.9630144912498};

    const { filterInfo: {type}} = props;
    this.category = type;
    
    makeObservable(this);
  }

  // Loading more filter data.  Mostly those that requires 'await'.
  async componentDidMount(): Promise<void> {
    const { staffIds } = this.props;
    this.getItemList(this.category, true);

    // Load the Vendor staff from DB.
    if(this.category === JOB_REQUEST_FILTERS.staff) {
      const staffUsers = await this.vendorsStore.getVendorStaff({onlyActive: true });
      runInAction(() => {
        this.staffNodes = this.formatVendorUsersTree(staffUsers, staffIds);
      });
      //console.log("*** staffNodes:", this.staffNodes.slice().map(item => ({...item}) ));
    }
  }

  // Function for sorting TagSite items by tag then by name.
  private compareTagSiteItem = (a: {tag: string, id: number, name: string}, b: {tag: string, id: number, name: string}): number => {
    let rc: number;
    if(!a.tag) { // null or empty
      if(!b.tag) {
        rc = 0;
      }
      else {
        rc = -1;
      }
    }
    else if(!b.tag) {
      if(!a.tag) {
        rc = 0;
      }
      else {
        rc = 1;
      }
    }
    else if(a.tag < b.tag) { // strings
      rc = -1;
    }
    else if(a.tag > b.tag) {
      rc = 1;
    }
    else { // same tag, check names.
      rc = a.name.localeCompare(b.name);
    }

    return rc;
  }

  // Get a list of staff, assets, or platforms.
  // Input:
  //   onlyActive: shows only active user.
  //   category: filter type such as staff, platforms, ...
  private getItemList = async (category: string, onlyActive = false) => {
    const {orderId, assetList} = this.props;

    if (category === JOB_REQUEST_FILTERS.staff) {
      const roles = [ROLES.productionTechnician, ROLES.observer, ROLES.pilot, ROLES.postProcessingTechnician]; // Get users from all roles.
      roles.forEach((roleName) => {
          this.vendorsStore.getCompanyUsers({ roleName, onlyActive }).then((users) => {
          this.users = {
            ...this.users,
            [roleName]: normalize(users, { id: VENDOR_USER_FIELDS.id, label: VENDOR_USER_FIELDS.fullName })
          };
        });
      });
    }
    else if(category === JOB_REQUEST_FILTERS.assets) {
      // let sites: Array<{id: number, name: string, tag: string}> = [];
      // if(orderId) {
      //   sites = await this.orderStore.getAssetList(orderId);
      // }
      const sites = assetList ? assetList : [];

      // Make sure the list is sorted alphabetically by site/asset name.
      if(sites.length > 0) {
        sites.sort((a,b) => a.name.localeCompare(b.name));
      }
      runInAction(() => {
        this.assetList = sites;
      });      
    }
    else if(category === JOB_REQUEST_FILTERS.assetsByMap) {
      let markers: Array<MapMarkerDTO> = [];
      if(orderId) {
        markers = await buildMarkersFromOrderID(orderId, []);
        this.markers = markers;
        this.bounds = computeBoundFromMarkers(markers);
        runInAction(() => {
          this.center = {
            lat: (this.bounds.latMin + this.bounds.latMax) / 2.0,
            lng: (this.bounds.lngMin + this.bounds.lngMax) / 2.0
          };
        }); 
      }
    }
    else if(category === JOB_REQUEST_FILTERS.tag1) {
      if(orderId) {
        const tagSiteList = await this.orderStore.getAssetList(orderId);
        runInAction(() => {
          this.tagSiteList = tagSiteList;
        });
      }

      // Sort the list alphabetically by tag then by site name.
      // Note: check why some site objs do not have a 'tag' field.
      if(this.tagSiteList.length > 0) {
        runInAction(() => {
          this.tagSiteList = this.tagSiteList.sort(this.compareTagSiteItem);
        });
      }

      //console.log("*** tagSiteList:", this.tagSiteList ? this.tagSiteList.slice().map(s => ({...s})) : []);
    }    // else if(category === JOB_REQUEST_FILTERS.platforms) {
    //   const params = {
    //     pagination: {
    //       page: 0,
    //       pageSize: 10,
    //     },
    //     sorting: {
    //       field: "serialNumber",
    //       type: SortingType.asc,
    //     },
    //   };

    //   const data = await this.platformStore.getList(params);
    //   if(data) {
    //     const { /*pageInfo,*/ items } = data;
    //     this.platforms = items;
    //   }
    // }
  }

  // Get list of Ids for staff selected in the staff tree.
  private getStaffIdsFrTree(): number[] {
    const selectedIds: number[] = [];
    if (this.staffTree) {
      const checkedIds = this.staffTree.getAllCheckedNodes();
      checkedIds.forEach(id => {
        const item = this.staffNodes.find(x => x.id.toString() == id);
        // Only take user not vendor Ids.
        if (item && (item.hasChildren == undefined || !item.hasChildren))
          selectedIds.push(Number(item.id));
      })
    }

    return selectedIds;
  }

  // Get list of Ids for sites selected in the tag tree.
  private getSiteIdsFrTree(): number[] {
    const selectedIds: number[] = [];
    if (this.tagTree) {
      const checkedIds = this.tagTree.getAllCheckedNodes();
      checkedIds.forEach(idStr => {
        const id = Number(idStr);
        // Collect only site nodes which have positive Ids.
        if(id > 0) {
          const item = this.tagSiteList.find(x => x.id == id);
          if(item) {
            selectedIds.push(id);
          }
        }
      })
    }

    //console.log("*** selectedId:", selectedIds);
    return selectedIds;
  }

  // Get list of Ids for assets selected in the multiSelect.
  private getAssetIdsFrMultiSelect(): number[] {

    if(this.assetSelect) {
      // Make sure the return values are numbers.
      return this.assetSelect.value as Array<number>;
    }

    return [];
  }

  // Get list of Ids for assets selected in the multiSelect.
  private getIdsFrMultiSelect(selComponent: MultiSelectComponent | null): number[] {

    if(selComponent != null) {
      // Make sure the return values are numbers.
      return selComponent.value as Array<number>;
    }

    return [];
  }

  // Bubble up a request to apply filtering to Order data.
  private handleSubmit = async () => {
    console.log("*** jr filter submit");
    const { onFilter, filterInfo: { type } } = this.props;

    let ids: number[];
    const keywords: string = ''; // keywords is no longer in use.
    if(this.category === JOB_REQUEST_FILTERS.staff) {
      ids = this.getStaffIdsFrTree(); // get values from syncfusion component directly.
    }
    else if(this.category === JOB_REQUEST_FILTERS.assets) {
      ids = this.getAssetIdsFrMultiSelect(); // get values from syncfusion component directly.
    }
    else if(this.category === JOB_REQUEST_FILTERS.assetsByMap) {
      ids = this.selectAssetIdsByMap;
    }
    else if(this.category === JOB_REQUEST_FILTERS.priority) {
      ids = this.getIdsFrMultiSelect(this.statusSelect); // get values from syncfusion component directly.
    }
    else if(this.category === JOB_REQUEST_FILTERS.flag) {
      ids = this.getIdsFrMultiSelect(this.flagSelect);
    }
    else if(this.category === JOB_REQUEST_FILTERS.planned) {
      const startDate = this.plannedDatePicker?.startDate; // get values from syncfusion component directly.
      const endDate = this.plannedDatePicker?.endDate;
      if(!startDate || !endDate) {
        ids = [];
      }
      else
      {
        // Convert start and end date to int.
        ids = [date2yyyymmdd(startDate), date2yyyymmdd(endDate)];
      }
    }
    else if(this.category === JOB_REQUEST_FILTERS.tag1) {
      ids = this.getSiteIdsFrTree();
    }
    else { // formVals.id can be 0 and not an array which is used as a way to clear a selection.
      ids = []; 
    }

    // Create obj to hold the filter settings (ie. ids and/or keywords).
    // 'assetsByMap' is not recognized by portal-api, treat it like 'assets'
    const items: FilterItems = {
      type: type == JOB_REQUEST_FILTERS.assetsByMap ? JOB_REQUEST_FILTERS.assets : type,
      ids: ids,
      keywords: keywords,
    }

    console.log("****** submitted filters:", items);

    onFilter(items);
    runInAction(() => {
      this.isSubmitting = false;
    });
  }

  // Trigger when the dialog close icon is clicked on.
  private handleDlgClose = () => {
    console.log("*** close jr filter form.")
    const { onFilter } = this.props;
    // This call will clear the display flag and hide the filter dialog.
    onFilter({ type: "none", ids: [], keywords: ''});
  }

  // Change platform property names to the common format.
  private normalizePlatform = (platforms) => {
    return normalize(platforms, { id: PLATFORM_FIELDS.id, label: PLATFORM_FIELDS.serialNumber });
  }
  
  // Configure button(s) for the dialog based on filter categories (text search, or selections).
  private get controls() {
    const { filterInfo: { type } } = this.props;
    const classes = withStyles.getClasses(this.props);

    const _controls =  (
        <div className={classes.root}>
          <Flex alignItems="flex-start" className={cx({ [classes.rootInnerDisabled]: false })}>
            <Button
              {...getTestAttr(`filter-${type}`, `send-${type}`, 'btn')}
              className={classes.actionBtn}
              type="submit"
              text="Filter"
              color="primary"
              loading={this.isSubmitting}
              onClick={this.handleSubmit.bind(this)}
            />
          </Flex>
        </div>
      );
    return _controls;
  }

  /**
   * Lowercases the first character in the `string`.
   *
   * @param {String} string
   * @returns {String}
   */
  private lcFirst = (string) => {  
    if (typeof string !== 'string') { 
      return ''
    }
    if (string.length === 0) {
      return string
    }
    return string[0].toLowerCase() + string.slice(1)
  }

  // Format a list of treeitems to an array for showing a two-level tree in syncfusion:
  //   parent cells (vendors) followed by child cells (usernames).
  formatVendorUsersTree = (staffUsers: Array<VendorUsersDTO>, staffIds: Array<number> | undefined) : Array<any> => {
    const { ids } = this.props.filterInfo;

    let treeData: Array<any> = [];

    let curParentId = 1000000; // offset to avoid mixing up tagSite and user ids which could be in the same range.
    if (staffUsers) {
      staffUsers.forEach(vendor => {
        // vendor node.
        curParentId += 1;
        treeData.push({id: curParentId, text: vendor.vendorName, hasChildren: true});

        let itemCnt = 0;
        // user node.
        vendor.vendorUsers
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
          .forEach(user => {
            // Only show staffs that belong to this order.
            if(staffIds && staffIds.includes(Number(user.id))) {
              itemCnt += 1;
              if(ids?.includes(Number(user.id))) { // 'ids' holds Ids of the item currently selected.
                treeData.push({id: user.id, parentID: curParentId, text: user.fullName, checked: true}); // tick the checkbox.
              }
              else {
                treeData.push({id: user.id, parentID: curParentId, text: user.fullName, checked: false  });
              }
            }
          })

        if(itemCnt == 0) {
          treeData = treeData.filter(x => x.id != curParentId); // remove the vendor node if no user.
        }
      })      
    }

    return treeData;
  }

  // Format a list of TagSite items to an array for showing a two-level tree in syncfusion:
  //   parent cells (tags) followed by child cells (siteNames).
  formatTagSiteTree = (tagSites: Array<{tag: string, id: number, name: string}>) : Array<{id: number, parentID: number, text: string, checked: boolean}> => {
    const { ids } = this.props.filterInfo; // 'ids' holds Ids of the currently select items.

    const treeData: Array<any> = [];
    let curParentId = 0; 
    let oldTag = '<none>';
    if (tagSites) {
      // Iterate the tagSite array which is sorted by tag then by siteName.
      tagSites.forEach(tagSite => {
        // Hit a new tag.  Use it as a new tag node.
        const curTag = tagSite.tag ? tagSite.tag : '(none)'; // use 'none' for both undefined and ''.
        if(curTag !== oldTag) {
          oldTag = curTag
          curParentId -= 1; // Use negative id for tag node to avoid collisions with site Ids.
          treeData.push({id: curParentId, text: curTag, hasChildren: true});
        }

        // Add the site.  Preselect if id is in list of currently selected.
        if(ids?.includes(tagSite.id)) { 
          treeData.push({id: tagSite.id, parentID: curParentId, text: tagSite.name, checked: true}); // tick the checkbox.
        }
        else {
          treeData.push({id: tagSite.id, parentID: curParentId, text: tagSite.name, checked: false}); 
        }
      })      
    }

    return treeData;
  }

  // Check/unCheck all nodes in the staffTree
  onStaffCheckAllChange = (args: any) => {
    if(this.staffTree != undefined && this.staffCheckbox != undefined) {
      if (args.checked) {
        this.staffTree.checkAll();
        this.staffCheckbox.label = LABEL_CLEAR_ALL; 
      } 
      else {
        this.staffTree.uncheckAll();
        this.staffCheckbox.label = LABEL_SELECT_ALL
      }
    }
  }

  // When a node is checked/unchecked, find and reset the checked status of the check/uncheck-all checkbox.
  onStaffNodeChecked = (/*args: NodeCheckEventArgs*/) => {
    if(this.staffTree != undefined && this.staffCheckbox != undefined) {
      this.staffCheckbox.checked  = this.staffTree.getAllCheckedNodes().length == this.staffTree.getTreeData().length;
      this.staffCheckbox.label = this.staffCheckbox.checked ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL;
    }
  }

  // Check/unCheck all nodes in the tagTree
  onTagCheckAllChange = (args: any) => {
    if(this.tagTree != undefined && this.tagCheckbox != undefined) {
      if (args.checked) {
        this.tagTree.checkAll();
        this.tagCheckbox.label = LABEL_CLEAR_ALL; 
      } 
      else {
        this.tagTree.uncheckAll();
        this.tagCheckbox.label = LABEL_SELECT_ALL;
      }
    }
  }

  // When a node is checked/unchecked, find and reset the checked status of the check/uncheck-all checkbox.
  onTagNodeChecked = (/*args: NodeCheckEventArgs*/) => {
    if(this.tagTree != undefined && this.tagCheckbox != undefined) {
      this.tagCheckbox.checked  = this.tagTree.getAllCheckedNodes().length == this.tagTree.getTreeData().length;
      this.tagCheckbox.label = this.tagCheckbox.checked ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL;
    }
  }

  // When user selects a group of assets from AGMap, add them to a list for later return
  handelOnSelectAssetsByMap = (assetIds: Array<number>) => {
    this.selectAssetIdsByMap = [];
    assetIds.forEach(x => this.selectAssetIdsByMap.push(x));
    // console.log(`Selected assets ${this.selectAssetIdsByMap}`);
  }

  // Setup the form fields based on categories.
  private get fields() {
    const { filterInfo: { type, ids }, possibleFlags } = this.props;
    const classes = withStyles.getClasses(this.props);

    const curSelectedIds = cloneDeep(ids) as number[]; 
    //console.log("*** CHECK currently selected Ids:", curSelectedIds);
    const flagOptions: Array<Option> = possibleFlags ?? [];

    // Note: Displayed label is 'Status' but internal storage is 'Priority'
    const placeholder = (type === JOB_REQUEST_FILTERS.assets || type === JOB_REQUEST_FILTERS.assetsByMap) ? 'Select assets' :
        (type === JOB_REQUEST_FILTERS.tag1 ? 'Enter partial tag' :
        (type === JOB_REQUEST_FILTERS.priority ? 'Select status' :
          `Select ${type}`));

    // Get the options for the filter list.  Note: staff, asset, and tag do not use 'options'.
    const options = type === JOB_REQUEST_FILTERS.platforms ? this.normalizePlatform(this.platforms) : 
                    type === JOB_REQUEST_FILTERS.priority ? Object.entries(PRIORITY_ENUM).slice(0, Object.keys(PRIORITY_ENUM).length / 2).map(([key, value]) => ({ id: parseInt(key), label: value.toString().charAt(0).toUpperCase() + value.toString().slice(1).replace('_', ' ')})) : 
                    type === JOB_REQUEST_FILTERS.flag ? flagOptions :
                    [];

    // Append 'Complete' to Status filter.
    if(type === JOB_REQUEST_FILTERS.priority) {
      options.push({ id: PRIORITY_ENUM_Complete, label: JOB_REQUEST_STATUS_LABELS[JobRequestStatus.completed]});
    }

    const loading = type === JOB_REQUEST_FILTERS.platforms ? this.platformStore.loading.list : 
                    type === JOB_REQUEST_FILTERS.staff ? this.vendorsStore.loading.list : false;

    //const labelTxt = type === JOB_REQUEST_FILTERS.assets ? 'Asset name' : 'Tag';

    // staff dialog with two-level treeview for filtering by staff ids.
    if(type === JOB_REQUEST_FILTERS.staff) {
      //console.log("*** staffNodes:", this.staffNodes.slice().map(item => ({...item}) ));
      const isCheckAll = Check4CheckAll(this.staffNodes);
      const label = isCheckAll ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL
      const _fields = [{
        label: 'Select vendors or expand vendor nodes to select individuals',
        component: (
          <div className={classes.treeControlWrapper}>
            <Grid container>
              <Grid item style={{ marginLeft: 54, marginBottom: 5 }}>
                <CheckBoxComponent // prepend a check-/uncheck-all box on top of the tree view.
                  ref={(ref) => {this.staffCheckbox = ref}}
                  id="checkAllStaff"
                  checked={isCheckAll}
                  label={label}
                  change={this.onStaffCheckAllChange.bind(this)}
                />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item>
                <TreeViewComponent
                  id='sfTreeViewTasks'
                  ref = {(ref) => {this.staffTree = ref}}
                  fields={{dataSource: this.staffNodes, isChecked: 'checked'}}
                  showCheckBox={true}
                  nodeChecked={this.onStaffNodeChecked.bind(this)}
                />
              </Grid>
            </Grid>
          </div>
        ),
      }];

      return _fields;
    }
    // assets dialog with multiselection for filtering by asset ids.
    else if(type === JOB_REQUEST_FILTERS.assets) {
      // Note: Workaround for syncfusion MultiSelectComponent bug not showing preselected items.
      // This component is rendered twice. The first time, this.assetList is empty; therefore the preselected list is also empty.
      // The second time, this.assetList is populated, but MultiSelectComponent does not reprocess the preselected list (content of array changes, but the array ptr does not).
      // Must clone and assign the new array to value={} to render the preselected selection.
      const _fields = [{
        label: 'Select assets',
        component: (
          <MultiSelectComponent 
            id="selectAssets" 
            ref = {(ref) => {this.assetSelect = ref}}
            dataSource={this.assetList} 
            fields={{text: 'name', value: 'id'}} 
            value={this.assetList.length > 0 ? curSelectedIds : []}
            mode="CheckBox"
            filterType={'Contains'}  // 'StartsWith'
            showSelectAll={true}
            sortOrder='None'  // List already sorted by name.  Disable sortOrder.  Otherwise, list will be sorted by 'value' which is the 'id' field.
          >
            <Inject services={[CheckBoxSelection]}/>
          </MultiSelectComponent>        
        ),        
      }];

      return _fields;
    }
    else if(type === JOB_REQUEST_FILTERS.assetsByMap) {
      const _fields = [{
        label: 'Select assets',
        component: (
          <AGMap
            orderId={this.props.orderId ?? 0}
            sites={this.markers}
            selectedSiteIds={ids}
            bounds={this.bounds}
            mapSize={{width: 420, height: 420}}
            leftTopPadding={{left: 0, top: 0}}
            center={this.center}
            allowSelection={true}
            onSelectAssets={this.handelOnSelectAssetsByMap.bind(this)}
          />
        ),        
      }];

      return _fields;
    }
    else if(type === JOB_REQUEST_FILTERS.priority || type === JOB_REQUEST_FILTERS.flag) {
      const _fields = [{
        label: 'Select status flags',
        component: (
          <MultiSelectComponent 
            id="selectFlags"
            ref = {(ref) => {
              if(type === JOB_REQUEST_FILTERS.priority)
                this.statusSelect = ref;
              else
                this.flagSelect = ref;
            }}
            dataSource={options as Array<{id: number, label: string}>} 
            fields={{text: 'label', value: 'id'}} 
            value={curSelectedIds} // Must use cloneDeep. If use this.props.ids, the property will be wiped for some reasons and the component won't show preselected items.
            mode="CheckBox"
            filterType={'Contains'} // 'StartsWith'
            showSelectAll={true}
            sortOrder='None' // List already sorted by name. Disable sortOrder. Otherwise, list will be sorted by 'value' which is the 'id' field.
          >
            <Inject services={[CheckBoxSelection]}/>
        </MultiSelectComponent>        
        ),        
      }];

      return _fields;

    }
    // tag form with two-level treeview for filtering by tags.
    else if(type === JOB_REQUEST_FILTERS.tag1) {
      const tagTree = this.formatTagSiteTree(this.tagSiteList);
      //console.log("*** tagTree:", tagTree);
      const isCheckAll = Check4CheckAll(tagTree);
      const label = isCheckAll ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL;
      const _fields = [{
        label: 'Select tags or expand tag nodes to select sites',
        component: (
          <div className={classes.treeControlWrapper}>
            <Grid container>
              <Grid item style={{ marginLeft: 54, marginBottom: 5 }}>
                <CheckBoxComponent // prepend a check-/uncheck-all box on top of the tree view.
                  ref={(ref) => {this.tagCheckbox = ref}}
                  id="checkAllTags"
                  checked={isCheckAll}
                  label={label}
                  change={this.onTagCheckAllChange.bind(this)}
                />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item>
                <TreeViewComponent
                  id='sfTreeViewTags'
                  ref = {(ref) => {this.tagTree = ref}}
                  fields={{dataSource: tagTree, isChecked: 'checked'}}
                  showCheckBox={true}     
                  nodeChecked={this.onTagNodeChecked.bind(this)}       
                />
              </Grid>
            </Grid>
          </div>
        ),
      }];

      return _fields;
    }
    // Date picker for filter planned date by a date range.
    else if(type === JOB_REQUEST_FILTERS.planned) {
      //console.log("*** check plannedDate:", ids);

      let startDate: Date | undefined;
      let endDate: Date | undefined;

      if(ids && ids.length >= 2) {
        startDate = yyyymmdd2date(ids[0]);
        endDate = yyyymmdd2date(ids[1]);
      }

      const _fields = [{
        label: 'Select a date range',
        component: (
          <DateRangePickerComponent
            id='plannedDateRangePicker'
            ref = {(ref) => {this.plannedDatePicker = ref}}
            startDate={startDate}
            endDate={endDate}
          />
        ),
      }];

      return _fields;
    }
    else {
      const _fields = [{
        label: 'Filter',
        component: (
          <Typography variant='body1'>Unhandled filter type: {type}.</Typography>
          // platform form with SelectionBox for filtering by selections (not currently in use).
          // <Select
          //   fullWidth
          //   placeholder={ placeholder }
          //   multiple
          //   options={ options }
          //   loading={ loading }
          //   margin="none"
          //   field={this.formFilter.$('id')}
          //   serializerType={SerializerType.number}
          //   testAttributeArgs={{
          //     module: 'JobRequestTimeline',
          //     elementDescription: `by-${type}`,
          //   }}
          // />  
        ),
      }];

      return _fields;
    }
  }

  // Show the form for users to configure the categories of items to filters.
  render() {
    const { filterInfo: {type} } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    // Display label for priority is Status
    const typeLabel = type === JOB_REQUEST_FILTERS.priority ? JOB_REQUEST_FILTERS.status : type;

    return (
      <Grid container spacing={0}>
        <Grid item xs={12}> 
          <Dialog
            open
            classes={{ paper: classes.root }}
            disableEnforceFocus={true} // Need this so focus can be given to the syncfusion MultiselectComponent.
          >
            {/* Form header */}
            <DialogTitle 
              classes={{ root: classes.title }}
              heading={(
                <Flex alignItems="center">
                  <h1 className={classes.heading}>Filter by {this.lcFirst(JOB_REQUEST_FILTER_LABELS[typeLabel])}</h1>
                </Flex>
              )}
              onClose={this.handleDlgClose}
            />

            {/* Show the filter dialog */}
            <DialogContent className={classes.content}> 
              {this.fields.map((field) => <Field {...field} key={field.label} classes={{label: classes.fieldLabel, component: classes.fieldComponent}} />)}
            </DialogContent>

            { /* Button to submit form. */}
            <DialogActions className={classes.actions}>
              {this.controls}
            </DialogActions>
          </Dialog >
        </Grid>
      </Grid>
    );
  }
}

const JobRequestFilterStyled = withStyles(
  JobRequestFilter, 
  (theme, props) => ({
    "root": {
      width: 666,
      maxWidth: 'none',
      // maxHeight: 'none',
      margin: 0,
      border: 'none',
    },
    "rootInnerDisabled": {
      ...theme.shortcuts.disabled,
    },  
    "loading": {
      height: 235,
    },
    "title": {
      borderBottom: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "heading": {
      fontSize: 20,
      fontWeight: 500,
    },
    "closeIcon": {
      padding: 0,
    },
    "content": {
      padding: theme.spacing(0, 6, 6),
      fontSize: 15,
      minHeight: 'none',
      maxHeight: 'none',
    },
    "actions": {
      backgroundColor: theme.palette.colors.greyLight5,
      padding: theme.spacing(6),
      borderTop: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "actionBtn": {
      fontSize: 16,
      marginLeft: theme.spacing(4),
    },
    "form": {
      width: '100%',
    },
    "datumProjectionField": {
      width: '80%'
    },
    "collectionTitle": {
      fontSize: 25,
      fontWeight: 400,
      padding: theme.spacing(4, 0, 4, 0),
      margin: theme.spacing(0, 6, 0, 10),
      borderBottom: `1px solid ${theme.palette.colors.grey}`,
      boxShadow: '0 1px rgba(0, 0, 0, 0.16)',
    },
    "treeControlWrapper": {
        height: '300px',
        overflow: 'auto',
        border: '1px solid #dddddd',
        borderRadius: '3px',
    },
    "fieldLabel": {
      width: '30%',
      marginRight: theme.spacing(4),
    },
    "fieldComponent": {
      width: '65%'
    }
  })
);

export default JobRequestFilterStyled;